import { MerchFlowCoreRange } from "@CommonApi/merchandise";
import { RoutePostMerchflows } from "@CommonApi/merchflows";
import { type ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import { RouteGetCategories } from "@CommonApi/categories";
import type { ResponseGetMerchflowsFilters } from "@CommonApi/merchflows/filters";
import { RoutePostMerchflowsUhd, type ResponsePostMerchflowsUhd } from "@CommonApi/merchflows/uhd";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useMerchflowsApi } from "src/api/merchflows";
import { useLoaders } from "src/components/InlineLoader";
import { FileUploader } from "src/components/FileUploader/FileUploader";
import { useModals } from "src/components/Modals";
import { modalsAtom } from "src/components/Modals/store/atoms";
import {
  Button,
  DateInput,
  Flex,
  HorizontalDivider,
  Input,
  Radios,
  Select,
  Text,
  VerticalDivider,
} from "src/elements";
import { useImportPlanogramsModal } from "src/modals/ImportPlanograms/store/hooks";
import { useImportPlanogramsFromPsaModal } from "src/modals/ImportPlanogramsFromPsa/store/hooks";
import type { MerchflowReviewStep } from "../store/types";

interface Props {
  setStep: (step: MerchflowReviewStep) => void;
  merchflowId: number | null;
  setMerchflowId: (merchflowId: number | null) => void;
  name: string;
  setName: (name: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  goLiveDate: Date | null;
  setGoLiveDate: (goLiveDate: Date | null) => void;
  deactivationDate: Date | null;
  setDeactivationDate: (goDeactivationDate: Date | null) => void;
  coreRange: MerchFlowCoreRange;
  setCoreRange: (coreRange: MerchFlowCoreRange) => void;
  category: string | null;
  setCategory: (category: string | null) => void;
  subcategory: string | null;
  setSubcategory: (subCategory: string | null) => void;
  availableCategories: ResponseGetMerchflowsCategories | null;
  setAvailableFilters: (availableFilters: ResponseGetMerchflowsFilters | null) => void;
  simulationId?: number;
  generationMode: "UHD" | "LD" | null;
  setGenerationMode: (mode: "UHD" | "LD" | null) => void;
  psaFile: File | null;
  setPsaFile: (file: File | null) => void;
  onUhdFlowCreated: (response: ResponsePostMerchflowsUhd) => void;
}

export const Step1Details = ({
  setStep,
  merchflowId,
  setMerchflowId,
  name,
  setName,
  notes,
  setNotes,
  goLiveDate,
  setGoLiveDate,
  deactivationDate,
  setDeactivationDate,
  coreRange,
  setCoreRange,
  category,
  setCategory,
  subcategory,
  setSubcategory,
  availableCategories,
  setAvailableFilters,
  simulationId,
  generationMode,
  setGenerationMode,
  psaFile,
  setPsaFile,
  onUhdFlowCreated,
}: Props) => {
  const { postMerchflowsApi, postMerchflowsUhdApi, getMerchflowsFiltersApi } = useMerchflowsApi();
  const { hasLoader } = useLoaders();
  const { closeModal: _closeModal } = useModals();
  const { openImportPlanogramsModal } = useImportPlanogramsModal();
  const { openImportPlanogramsFromPsaModal } = useImportPlanogramsFromPsaModal();

  const [selectionMode, setSelectionMode] = useState<"CATEGORY" | "IMPORT" | null>(null);
  const modals = useAtomValue(modalsAtom);

  const isCategoriesLoading = hasLoader(RouteGetCategories);
  const isCreateReviewLoading = hasLoader(RoutePostMerchflows) || hasLoader(RoutePostMerchflowsUhd);

  useEffect(() => {
    const isImportModalOpen =
      modals.includes("ModalImportPlanograms") || modals.includes("ModalImportPlanogramsFromPsa");
    if (!isImportModalOpen && selectionMode === "IMPORT") {
      setSelectionMode(null);
    }
  }, [modals, selectionMode]);

  const minDeactivationDate = useMemo(() => {
    if (!goLiveDate) {
      return new Date("2000-01-01");
    }

    const deactivationDate = new Date(goLiveDate);
    deactivationDate.setDate(deactivationDate.getDate() + 1);

    return deactivationDate;
  }, [goLiveDate]);

  const categories = useMemo(() => {
    if (availableCategories) {
      return Object.keys(availableCategories);
    }

    return [];
  }, [availableCategories]);

  const subCategories = useMemo(() => {
    if (availableCategories) {
      // All possible sub-categories.
      return Object.values(availableCategories).reduce(
        (allSubCategories, categorySubCategories) => [
          ...allSubCategories,
          ...categorySubCategories,
        ],
        [],
      );
    }

    return [];
  }, [availableCategories]);

  // Effect to update category based on selected sub-category.
  useEffect(() => {
    if (!availableCategories || !subcategory) return;

    for (const _category of Object.keys(availableCategories)) {
      if (availableCategories[_category].includes(subcategory)) {
        if (_category !== category) {
          setCategory(_category);
        }
      }
    }
  }, [subcategory]);

  // Effect to update sub-category based on selected category.
  useEffect(() => {
    if (!availableCategories || !category) return;
    // Check is correct sub-category is already assigned.
    for (const _subcategory of availableCategories[category]) {
      if (_subcategory === subcategory) {
        return;
      }
    }

    // Assign first sub-category from selected category.
    if (availableCategories[category].length > 0) {
      setSubcategory(availableCategories[category][0]);
    }
  }, [category]);

  const closeModal = () => {
    _closeModal("ModalMerchflowReview");
  };

  const nextStep = () => {
    setStep("STEP_2_FILTERS");
  };

  const createReview = async () => {
    if (!category || !subcategory || !name || !goLiveDate) return;

    const response = await postMerchflowsApi({
      retailer_category_code: category,
      category_code: subcategory,
      name,
      notes,
      core_range: {
        option: coreRange,
      },
      go_live_date: goLiveDate.toISOString(),
      deactivation_date: deactivationDate?.toISOString() || null,
      simulation_id: simulationId,
    });

    if (response) {
      setMerchflowId(response.id);
      nextStep();
      setAvailableFilters(
        await getMerchflowsFiltersApi({
          retailerCategoryCode: response.retailer_category_code,
          categoryCode: response.category_code,
        }),
      );
    }
  };

  const createUhdFlow = async () => {
    if (!category || !subcategory || !name || !goLiveDate) return;

    const response = await postMerchflowsUhdApi({
      name,
      notes,
      goLiveDate: goLiveDate.toISOString(),
      deactivationDate: deactivationDate?.toISOString() ?? null,
      coreRangeOption: coreRange,
      categoryCode: subcategory,
      retailerCategoryCode: category,
      psaFile: psaFile ?? undefined,
    });

    if (response) {
      onUhdFlowCreated(response);
    }
  };

  const startNewFlow = () => {
    if (generationMode === "UHD") {
      void createUhdFlow();
    } else {
      void createReview();
    }
  };

  return (
    <Flex column flexGrow={1} overflow="hidden">
      <Flex padding="20px" gap="20px" justify="center" overflow="auto" flexGrow={1} minHeight="0">
        <Flex column gap="20px">
          <Text variant="body1">1. Please input your Flow details.</Text>

          <Input title="Name" value={name} setValue={setName} isRequired />

          <Flex gap="20px">
            <DateInput
              width="180px"
              title="Go-Live Date"
              value={goLiveDate}
              setValue={setGoLiveDate}
              min={new Date("2000-01-01")}
              isRequired
            />

            <DateInput
              width="180px"
              title="Deactivation Date"
              value={deactivationDate}
              setValue={setDeactivationDate}
              min={minDeactivationDate}
              error={
                goLiveDate !== null &&
                deactivationDate !== null &&
                deactivationDate < goLiveDate &&
                "Deactivation date needs to be after go-live date"
              }
            />
          </Flex>

          <Input title="Notes" value={notes} setValue={setNotes} />

          <Radios
            label="2. Please select your Core Range options."
            direction="column"
            options={[
              {
                label: "All products in Core Range",
                value: MerchFlowCoreRange.ALL,
              },
              {
                label: "No products in Core Range",
                value: MerchFlowCoreRange.NONE,
              },
              {
                label: "All allocated products in Core Range",
                value: MerchFlowCoreRange.PARTIAL,
              },
            ]}
            value={coreRange}
            setValue={setCoreRange}
          />
        </Flex>

        <VerticalDivider />

        <Flex column gap="20px">
          <Text variant="body1">3. Please select your dataset.</Text>

          <Flex gap="20px">
            <Select
              width="180px"
              title="Category"
              value={category}
              placeholder="Category"
              options={categories.sort().map((category) => ({
                label: category,
                value: category,
              }))}
              setValue={(value) => {
                setCategory(value);
                setSelectionMode("CATEGORY");
              }}
              isLoading={isCategoriesLoading}
              isRequired={selectionMode !== "IMPORT"}
              isSearchable
              isDisabled={selectionMode === "IMPORT"}
            />

            <Select
              width="180px"
              title="Sub-Category"
              value={subcategory}
              setValue={(value) => {
                setSubcategory(value);
                setSelectionMode("CATEGORY");
              }}
              placeholder="Sub-Category"
              options={subCategories.sort().map((subCategory) => ({
                label: subCategory,
                value: subCategory,
              }))}
              isLoading={isCategoriesLoading}
              isRequired={selectionMode !== "IMPORT"}
              isSearchable
              isDisabled={selectionMode === "IMPORT"}
            />
          </Flex>

          <Text variant="body1">4a. Please select your generation mode:</Text>

          <Radios
            label=""
            direction="column"
            options={[
              {
                label: "Auto-Mudmap Engine (Ultra-high density)",
                value: "UHD",
              },
              {
                label: "Mudmap Layout Engine (Low density)",
                value: "LD",
              },
            ]}
            value={generationMode}
            setValue={(value) => {
              if (value !== "UHD") setPsaFile(null);
              if (value === "UHD" || value === "LD") {
                setGenerationMode(value);
              } else {
                setGenerationMode(null);
              }
              setSelectionMode("CATEGORY");
            }}
            isDisabled={selectionMode === "IMPORT"}
          />

          {generationMode === "UHD" && (
            <Flex column gap="8px">
              <Text variant="body2" color={undefined}>
                Base Planogram (optional — upload a .psa file to use as base POG):
              </Text>
              <FileUploader
                fileLabel="PSA base planogram"
                fileType="psa"
                isLoading={false}
                onChange={setPsaFile}
              />
            </Flex>
          )}

          <Flex align="center" gap="10px" width="100%">
            <HorizontalDivider />
            <Text variant="body2" color={undefined}>
              OR
            </Text>
            <HorizontalDivider />
          </Flex>

          <Flex column gap="10px">
            <Text variant="body1">4b. Import existing planogram data:</Text>

            <Button
              variant="inverted"
              color="greenSmoke"
              onClick={() => {
                if (!name || !goLiveDate) {
                  return;
                }
                setSelectionMode("IMPORT");
                setGenerationMode(null);
                setPsaFile(null);
                openImportPlanogramsModal({
                  name,
                  goLiveDate,
                  deactivationDate,
                  notes,
                  coreRange,
                });
              }}
              isDisabled={!name || !goLiveDate}
            >
              Import planograms (.csv)
            </Button>
          </Flex>

          <Flex column gap="10px">
            <Text variant="body1">4c. Import existing planogram data from PSA:</Text>

            <Button
              variant="inverted"
              color="greenSmoke"
              onClick={() => {
                if (!name || !goLiveDate || !availableCategories) {
                  return;
                }

                setSelectionMode("IMPORT");
                setGenerationMode(null);
                setPsaFile(null);
                openImportPlanogramsFromPsaModal({
                  name,
                  goLiveDate,
                  deactivationDate,
                  notes,
                  coreRange,
                  availableCategories,
                  category,
                  subcategory,
                });
              }}
              isDisabled={!name || !goLiveDate || !availableCategories}
            >
              Import planograms from PSA
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex gap="10px" justify="right" gridColumn="1 / -1">
        <Button variant="inverted" color="red" onClick={closeModal}>
          Cancel
        </Button>

        {merchflowId === null ? (
          <Button
            isDisabled={
              !name ||
              !goLiveDate ||
              (selectionMode === "CATEGORY" && (!category || !subcategory)) ||
              (selectionMode === "CATEGORY" && !generationMode) ||
              (selectionMode !== "CATEGORY" && selectionMode !== "IMPORT") ||
              (goLiveDate !== null && deactivationDate !== null && deactivationDate < goLiveDate)
            }
            isLoading={isCreateReviewLoading}
            onClick={startNewFlow}
          >
            Start New Flow
          </Button>
        ) : (
          <Button onClick={nextStep}>Next</Button>
        )}
      </Flex>
    </Flex>
  );
};
